import { set } from "date-fns";
import React, { createContext, useState, useContext } from "react";
import { callAPI } from "./api";
import localforage from "localforage";

const UserContext = createContext();

export const useUser = () => useContext(UserContext);

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profilePicture, setProfilePicture] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [userLoading, setUserLoading] = useState(true);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [showTabs, setShowTabs] = useState(false);
  const [currentTab, setCurrentTab] = useState(null);

  // Role names returned by the API's GET /api/me/roles (resolved from Azure AD group IDs server-side).
  const ROLE_CONFIG = {
    admin: ["Admin"],
    user: ["User"],
    finance: ["Finance"],
    editor: ["Editor"],
    manager: ["Manager"],
  };

  const checkAccess = (requiredRoles) =>
    requiredRoles.some((role) => userRoles.includes(role));

  const getFormattedName = () => {
    if (user && user.displayName) {
      const nameParts = user.displayName.split(" ");
      const firstName = nameParts[1] || "";
      const lastName = nameParts[0] || "";
      return `${firstName} ${lastName}`.replace(/,\s*$/, "").trim(); // Remove trailing comma and extra spaces
    }
    return "";
  };

  const getAvatar = async () => {
    if (!user?.mail || !user.displayName) return;

    const cacheKey = `avatar_${user.mail}`;

    try {
      const cachedAvatar = await localforage.getItem(cacheKey);

      if (cachedAvatar) {
        setProfilePicture(cachedAvatar);
        return;
      }

      const response = await callAPI(
        `ticketing/jira/avatar?emailAddress=${encodeURIComponent(user.mail)}`,
        "GET",
        null,
        true
      );

      const isDefault = response === "default" || !response;
      const avatarUrl = isDefault
        ? `https://ui-avatars.com/api/?name=${encodeURIComponent(getFormattedName())}&background=random`
        : response;

      await localforage.setItem(cacheKey, avatarUrl);
      setProfilePicture(avatarUrl);
    } catch (error) {
      console.error("Error fetching avatar:", error);
      const fallback = `https://ui-avatars.com/api/?name=${encodeURIComponent(getFormattedName())}&background=random`;
      await localforage.setItem(cacheKey, fallback);
      setProfilePicture(fallback);
    }
  };

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        toolsOpen,
        setToolsOpen,
        profilePicture,
        setProfilePicture,
        userRoles,
        setUserRoles,
        ROLE_CONFIG,
        checkAccess,
        getFormattedName,
        userLoading,
        setUserLoading,
        setShowTabs,
        getAvatar,
        showTabs,
        currentTab,
        setCurrentTab,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
